import React, { createContext, useCallback, useContext, useState, useEffect } from 'react';
import { translations } from '../utils/translations';

const LanguageContext = createContext();

const LANGUAGE_STORAGE_KEY = '@website_language';

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('en');
  const [isRTL, setIsRTL] = useState(false);

  const loadLanguage = useCallback(() => {
    try {
      const savedLanguage = localStorage.getItem(LANGUAGE_STORAGE_KEY);
      if (savedLanguage && (savedLanguage === 'en' || savedLanguage === 'ar')) {
        setLanguage(savedLanguage);
        updateRTL(savedLanguage === 'ar');
      }
    } catch (error) {
      console.error('Error loading language:', error);
    }
  }, []);

  useEffect(() => {
    loadLanguage();
  }, [loadLanguage]);

  const updateRTL = (rtl) => {
    setIsRTL(rtl);
    // Update document direction
    if (rtl) {
      document.documentElement.setAttribute('dir', 'rtl');
      document.documentElement.setAttribute('lang', 'ar');
    } else {
      document.documentElement.setAttribute('dir', 'ltr');
      document.documentElement.setAttribute('lang', 'en');
    }
  };

  const changeLanguage = (newLanguage) => {
    if (newLanguage !== 'en' && newLanguage !== 'ar') {
      console.warn('Unsupported language:', newLanguage);
      return;
    }

    try {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, newLanguage);
      setLanguage(newLanguage);
      updateRTL(newLanguage === 'ar');
    } catch (error) {
      console.error('Error saving language:', error);
    }
  };

  const t = (key, variables = {}) => {
    const keys = key.split('.');
    let value = translations[language];

    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k];
      } else {
        value = undefined;
        break;
      }
    }

    // If key is missing in current language, fall back to English if possible.
    let translatedValue = value;
    if (translatedValue === undefined || translatedValue === null) {
      translatedValue = (() => {
        let enValue = translations.en;
        for (const k of keys) {
          if (enValue && typeof enValue === 'object') enValue = enValue[k];
          else return undefined;
        }
        return enValue;
      })();
    }

    const cleanKeyLabel = () => {
      const last = key.split('.').pop() || key;
      // turn camelCase into words: allUsers -> all Users
      const spaced = last.replace(/([a-z])([A-Z])/g, '$1 $2');
      const normalized = spaced.replace(/[_-]/g, ' ');
      return normalized.charAt(0).toUpperCase() + normalized.slice(1);
    };

    if (translatedValue === undefined || translatedValue === null) translatedValue = cleanKeyLabel();

    // Never return an object (e.g. t('seatAllocation') for a namespace) — React can't render it
    if (typeof translatedValue !== 'string') {
      return cleanKeyLabel();
    }

    Object.keys(variables).forEach(varKey => {
      translatedValue = translatedValue.replace(new RegExp(`\\{${varKey}\\}`, 'g'), variables[varKey]);
    });

    return translatedValue;
  };

  return (
    <LanguageContext.Provider value={{ language, changeLanguage, t, isRTL }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
